import * as XLSX from 'xlsx';
import { COLORS } from '@/lib/furnitureData';

// ---------- Справочники ----------

// Листовые материалы (всё, что пилится из листа)
export const SHEET_MATERIALS = {
  chipboard:         { nameRu: 'ЛДСП 16 мм', thickness: 16, sheetL: 2750, sheetW: 1830 },
  shelving_ldsp_16:  { nameRu: 'ЛДСП 16 мм', thickness: 16, sheetL: 2750, sheetW: 1830 },
  shelving_ldsp_22:  { nameRu: 'ЛДСП 22 мм', thickness: 22, sheetL: 2750, sheetW: 1830 },
  mdf:               { nameRu: 'МДФ 16 мм',  thickness: 16, sheetL: 2800, sheetW: 2070 },
  solid_wood:        { nameRu: 'Массив 20 мм', thickness: 20, sheetL: 3000, sheetW: 1000 },
};

export const HDF_BACK = { nameRu: 'ХДФ 3 мм', color: 'Белый', thickness: 3, sheetL: 2800, sheetW: 2070 };

// Каталог фурнитуры с артикулами
export const HARDWARE_CATALOG = {
  minifix_d15:        { article: 'M-FIX-D15',     nameRu: 'Минификс D15, для плиты 15-16 мм' },
  minifix_rod:        { article: 'M-FIX-ROD',     nameRu: 'Минификс шток (дюбель)' },
  minifix_cap:        { article: 'M-FIX-CAP',     nameRu: 'Заглушка минификса в цвет' },
  dowel_30x8:         { article: 'DWL-30-8',      nameRu: 'Шкант 8×30 мм' },
  dowel_50x8:         { article: 'DWL-50-8',      nameRu: 'Шкант 8×50 мм' },
  leg_decor_h145:     { article: 'ADJ19.150.BL',  nameRu: 'Опора декоративная H145+8мм, чёрный матовый' },
  felt_pad_d14:       { article: 'H008-D14',      nameRu: 'Подпятник с гвоздём D14 мм, белый' },
  screw_pozi_35x16:   { article: 'SCR-3.5-16',    nameRu: 'Саморез Pozi 3.5×16 мм' },
  nail_tole_2x20:     { article: 'NAL-2-20',      nameRu: 'Гвоздь толевый 2×20 мм' },
  hinge_soft:         { article: 'HNG-SOFT',      nameRu: 'Петля с доводчиком, 4-отверстная' },
  hinge_standard:     { article: 'HNG-STD',       nameRu: 'Петля обычная, 4-отверстная' },
  handle_classic:     { article: 'HDL-CLS-128',   nameRu: 'Ручка классическая, 128 мм' },
  handle_modern:      { article: 'HDL-MOD-160',   nameRu: 'Ручка современная, 160 мм' },
  handle_hidden:      { article: 'HDL-HID',       nameRu: 'Ручка скрытая (профиль)' },
  slide_ball:         { article: 'SLD-BALL-450',  nameRu: 'Направляющие шариковые полного выдвижения, 450 мм (пара)' },
  slide_roller:       { article: 'SLD-ROLL-400',  nameRu: 'Направляющие роликовые, 400 мм (пара)' },
  rafix:              { article: 'RFX-ST',        nameRu: 'Рафикс со штоком' },
};

const colorNameOf = (id) => (COLORS.find(c => c.id === id) || {}).nameRu || '—';

// ---------- Утилиты ----------

const toMm = (cm) => Math.round(Number(cm || 0) * 10);

function sheetMatKey(mat, colorId) {
  if (!mat) return null;
  return `${mat.nameRu}|${colorNameOf(colorId)}`;
}

function hdfKey() {
  return `${HDF_BACK.nameRu}|${HDF_BACK.color}`;
}

// ---------- Декомпозиция модуля в листовые детали ----------
// Единицы измерения: в модели — см, в спецификации — мм.
// Кромка: L1/L2 — на торцах по длине (мм), W1/W2 — на торцах по ширине.
// 0.4 — скрытая, 2.0 — фасадная видимая.

function decomposeModule(m, { skipImplicitBackPanel = false } = {}) {
  const parts = [];
  const hardware = {};
  const addHW = (id, qty) => { hardware[id] = (hardware[id] || 0) + qty; };

  const mat = SHEET_MATERIALS[m.material];
  const colorId = m.color_id || 'light_oak';
  const t = mat?.thickness || 16;
  const Wmm = toMm(m.width);
  const Hmm = toMm(m.height);
  const Dmm = toMm(m.depth);

  const hasHandle = (m.hardware || []).find(hw => String(hw).startsWith('handle_'));

  switch (m.type) {
    case 'body':
    case 'frame': {
      if (!mat) break;
      // Стандартный корпус: крыша накладная сверху, дно вкладное между стенками, 2 стенки, задняя стенка ХДФ.
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'крыша накладная',  L: Wmm,         W: Dmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0.4, W2: 0.4 } });
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'дно вкладное',     L: Wmm - 2 * t, W: Dmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0.4, W2: 0.4 } });
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'стенка левая',     L: Hmm - t,     W: Dmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0.4, W2: 0.4 } });
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'стенка правая',    L: Hmm - t,     W: Dmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0.4, W2: 0.4 } });
      if (!skipImplicitBackPanel) {
        parts.push({ matKey: hdfKey(), material: HDF_BACK, colorId: null,
          name: 'задняя стенка',  L: Wmm - 4,     W: Hmm - 4, qty: 1, edges: null });
      }

      addHW('minifix_d15', 8);
      addHW('minifix_rod', 8);
      addHW('minifix_cap', 8);
      addHW('dowel_30x8', 16);
      addHW('leg_decor_h145', 4);
      addHW('screw_pozi_35x16', 16);
      addHW('nail_tole_2x20', 30);
      break;
    }
    case 'door': {
      if (!mat) break;
      // Фасад: кромка 2мм по всем сторонам.
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: m.name_ru || 'фасад', L: Hmm, W: Wmm, qty: 1, edges: { L1: 2, L2: 2, W1: 2, W2: 2 } });
      addHW('hinge_soft', 2);
      if (hasHandle) addHW(hasHandle, 1);
      break;
    }
    case 'shelf':
    case 'board': {
      if (!mat) break;
      // Полка: ширина×глубина, передняя кромка 0.4.
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: m.name_ru || 'полка', L: Wmm, W: Dmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0, W2: 0 } });
      addHW('minifix_d15', 4);
      addHW('minifix_rod', 4);
      addHW('minifix_cap', 4);
      addHW('dowel_30x8', 4);
      break;
    }
    case 'back_panel': {
      parts.push({ matKey: hdfKey(), material: HDF_BACK, colorId: null,
        name: 'задняя стенка', L: Wmm, W: Hmm, qty: 1, edges: null });
      addHW('nail_tole_2x20', 20);
      break;
    }
    case 'drawer': {
      if (!mat) break;
      // Ящик = фасад + 2 боковины + задник + дно из ХДФ.
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'фасад ящика',     L: Wmm,         W: Hmm, qty: 1, edges: { L1: 2, L2: 2, W1: 2, W2: 2 } });
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'боковина ящика',  L: Dmm,         W: Hmm, qty: 2, edges: { L1: 0.4, L2: 0, W1: 0, W2: 0 } });
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'задник ящика',    L: Wmm - 2 * t, W: Hmm, qty: 1, edges: { L1: 0.4, L2: 0, W1: 0, W2: 0 } });
      parts.push({ matKey: hdfKey(), material: HDF_BACK, colorId: null,
        name: 'дно ящика',       L: Wmm - 4,     W: Dmm - 4, qty: 1, edges: null });

      addHW('slide_ball', 1);
      addHW('screw_pozi_35x16', 12);
      addHW('nail_tole_2x20', 15);
      if (hasHandle) addHW(hasHandle, 1);
      break;
    }
    case 'top_panel':
    case 'tabletop':
    case 'countertop': {
      if (!mat) break;
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: m.name_ru || 'столешница', L: Wmm, W: Dmm, qty: 1, edges: { L1: 2, L2: 2, W1: 2, W2: 2 } });
      addHW('dowel_50x8', 4);
      addHW('felt_pad_d14', 4);
      break;
    }
    case 'headboard': {
      if (!mat) break;
      parts.push({ matKey: sheetMatKey(mat, colorId), material: mat, colorId,
        name: 'изголовье', L: Wmm, W: Hmm, qty: 1, edges: { L1: 2, L2: 2, W1: 2, W2: 2 } });
      addHW('dowel_50x8', 4);
      break;
    }
    // leg, crossbar, bracket, hanging_rod, slat → металл или фурнитура, не листовые.
    case 'leg': {
      // Покупная металлическая/деревянная ножка — учитываем как опору.
      addHW('leg_decor_h145', 1);
      break;
    }
    default:
      break;
  }

  return { parts, hardware };
}

// ---------- Сводная спецификация ----------

export function generateSpecification(modules) {
  const partsByMaterial = new Map();
  const hardware = {};
  const hasExplicitBackPanel = modules.some(m => m.type === 'back_panel');

  for (const m of modules) {
    const { parts, hardware: hw } = decomposeModule(m, { skipImplicitBackPanel: hasExplicitBackPanel });
    for (const p of parts) {
      if (p.L <= 0 || p.W <= 0) continue;
      if (!partsByMaterial.has(p.matKey)) {
        partsByMaterial.set(p.matKey, { material: p.material, colorId: p.colorId, items: [] });
      }
      const bucket = partsByMaterial.get(p.matKey).items;
      const existing = bucket.find(x => x.name === p.name && x.L === p.L && x.W === p.W
        && JSON.stringify(x.edges || {}) === JSON.stringify(p.edges || {}));
      if (existing) existing.qty += p.qty;
      else bucket.push({ ...p });
    }
    for (const [id, qty] of Object.entries(hw)) {
      hardware[id] = (hardware[id] || 0) + qty;
    }
  }

  const materials = [];
  for (const group of partsByMaterial.values()) {
    const sheetAreaMm2 = group.items.reduce((s, p) => s + p.L * p.W * p.qty, 0);
    materials.push({
      ...group,
      items: group.items.sort((a, b) => b.L * b.W - a.L * a.W),
      sheetAreaMm2,
      sheetAreaSqm: sheetAreaMm2 / 1_000_000,
    });
  }
  // Сначала ЛДСП/МДФ/Массив, потом ХДФ
  materials.sort((a, b) => (a.material === HDF_BACK ? 1 : 0) - (b.material === HDF_BACK ? 1 : 0));

  const hardwareList = Object.entries(hardware)
    .map(([id, qty]) => {
      const cat = HARDWARE_CATALOG[id] || { article: '—', nameRu: id };
      return { id, qty, ...cat };
    })
    .sort((a, b) => a.nameRu.localeCompare(b.nameRu, 'ru'));

  return { materials, hardware: hardwareList };
}

// ---------- Экспорт в XLSX ----------

function formatEdge(v) {
  if (v === undefined || v === null) return '';
  if (v === 0) return '';
  return v.toString().replace('.', ',');
}

export function exportSpecificationXLSX(modules, { projectName = 'Спецификация', orderNumber = '' } = {}) {
  const spec = generateSpecification(modules);
  const wb = XLSX.utils.book_new();
  const aoa = [];

  const now = new Date();
  const dateStr = now.toLocaleDateString('ru-RU') + ' ' + now.toLocaleTimeString('ru-RU');
  aoa.push([`${projectName}${orderNumber ? ' № ' + orderNumber : ''} · ${dateStr}`]);
  aoa.push([]);

  // Блоки по материалам
  spec.materials.forEach((mat) => {
    const title = mat.material === HDF_BACK
      ? `${HDF_BACK.nameRu}, ${HDF_BACK.color}`
      : `${colorNameOf(mat.colorId)}, ${mat.material.nameRu}`;
    aoa.push([title]);
    aoa.push(['№', 'Наименование', 'L', 'W', 'Кол-во', 'L1', 'L2', 'W1', 'W2']);
    mat.items.forEach((p, i) => {
      const e = p.edges || {};
      aoa.push([
        i + 1,
        p.name,
        p.L,
        p.W,
        p.qty,
        formatEdge(e.L1),
        formatEdge(e.L2),
        formatEdge(e.W1),
        formatEdge(e.W2),
      ]);
    });
    // Итого площадь
    aoa.push(['', `Итого ${mat.sheetAreaSqm.toFixed(3)} м²`, '', '', mat.items.reduce((s, x) => s + x.qty, 0), '', '', '', '']);
    aoa.push([]);
    aoa.push([]);
  });

  // Фурнитура
  aoa.push(['Фурнитура']);
  aoa.push(['№', 'Наименование', 'Артикул', 'Кол-во']);
  spec.hardware.forEach((h, i) => {
    aoa.push([i + 1, h.nameRu, h.article, h.qty]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Ширины колонок (сим-приближение)
  ws['!cols'] = [
    { wch: 4 },   // №
    { wch: 36 },  // Наименование
    { wch: 8 },   // L
    { wch: 8 },   // W
    { wch: 8 },   // Кол-во
    { wch: 5 },   // L1
    { wch: 5 },   // L2
    { wch: 5 },   // W1
    { wch: 5 },   // W2
  ];

  // Объединения для заголовков-«шапок»
  const merges = [];
  // Первая строка — заголовок документа
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } });
  // Заголовок-название каждого материала и слова "Фурнитура"
  aoa.forEach((row, idx) => {
    if (row.length === 1 && typeof row[0] === 'string' && row[0].length > 0) {
      merges.push({ s: { r: idx, c: 0 }, e: { r: idx, c: 8 } });
    }
  });
  ws['!merges'] = merges;

  XLSX.utils.book_append_sheet(wb, ws, 'Спецификация');

  const safeName = (projectName || 'specification').replace(/[^\wа-яА-Я0-9-_ ]/gi, '').slice(0, 60);
  XLSX.writeFile(wb, `${safeName || 'specification'}.xlsx`);

  return spec;
}
